import z from "zod";
import createSchemaUtils from "../../helpers/createSchemaUtils";
import { PlainObjectSchema } from "../PlainObject";
import {
    QueryEndpointSchema,
    QuickSearchFilterSchema,
    SortOptionSchema
} from "../QueryModels";

export * from "./errors";

export type EntityObject<T> = T & {
    $$Id: string;
    $$Entity: string;
    $$DisplayName: string;
};

const EntityModelSchema = z.object({
    Name: z.string().nonempty(),
    DisplayProperty: z.string().nonempty().optional(),
    ValueProperty: z.string().nonempty(),
    Endpoints: z.object({
        Fetch: QueryEndpointSchema.optional(),
        Create: QueryEndpointSchema.optional(),
        Update: QueryEndpointSchema.optional(),
        Delete: QueryEndpointSchema.optional(),
    }).optional(),
    QuickSearch: QuickSearchFilterSchema.optional(),
    SortOptions: SortOptionSchema.array().optional(),
    Fields: z.record(z.string().nonempty(), z.lazy(() => EntityFieldSchema)),
});
export type EntityModel = z.infer<typeof EntityModelSchema>;
export const EntityModel = createSchemaUtils(EntityModelSchema);

//#region Basic Entity Fields
const BasicEntityFieldSchema = z.object({
    field: z.string().nonempty(),
});
export const BooleanEntityFieldSchema = BasicEntityFieldSchema.extend({
    type: z.literal("boolean"),
    defaultValue: z.boolean().or(z.string()).optional(),
});
export type BooleanEntityField = z.infer<typeof BooleanEntityFieldSchema>;

export const DateEntityFieldSchema = BasicEntityFieldSchema.extend({
    type: z.literal("date"),
});
export type DateEntityField = z.infer<typeof DateEntityFieldSchema>;

export const NumberEntityFieldSchema = BasicEntityFieldSchema.extend({
    type: z.literal("number"),
    defaultValue: z.number().or(z.string()).optional(),
});
export type NumberEntityField = z.infer<typeof NumberEntityFieldSchema>;

export const StringEntityFieldSchema = BasicEntityFieldSchema.extend({
    type: z.literal("string"),
    defaultValue: z.string().optional(),
});
export type StringEntityField = z.infer<typeof StringEntityFieldSchema>;

export const UuidEntityFieldSchema = BasicEntityFieldSchema.extend({
    type: z.literal("uuid"),
});
export type UuidEntityField = z.infer<typeof UuidEntityFieldSchema>;
//#endregion

//#region Custom Entity Field
export const CustomEntityFieldSchema = z.object({
    type: z.literal("custom"),
    parse: z.function({
        input: z.tuple([
            PlainObjectSchema.describe("The raw data object returned from the data source."),
            PlainObjectSchema.describe("The current parsed entity object.")
        ]),
        output: z.unknown(),
    }),
});
/**
 * Represents a custom entity field in the data model.
 * The `parse` function is used to extract and transform the field's value
 * from the raw data object.
 * @example
 * ```ts
 * const CustomField: CustomEntityField = {
 *   type: "custom",
 *   parse(data: PlainObject, currentData: PlainObject) {
 *       return data["customField"] || "";
 *   }
 * };
 * ```
 */
export type CustomEntityField = z.infer<typeof CustomEntityFieldSchema>;
//#endregion

//#region Lookup Entity Field
export const LookupEntityFieldSchema = z.object({
    type: z.literal("lookup"),
    field: z.string()
        .describe("The field that contains the related entity ID. (i.e., personId)")
        .nonempty(),
    entity: z.string()
        .describe("The entity (table in sql terms) that this field is related to.")
        .nonempty(),
    relatedField: z.string()
        .describe("The field that contains the related entity details (foreignKey in sql terms). (i.e., personDetails, since personId is related to personDetails)")
        .nonempty(),
    attributes: z.array(z.enum(["ExpandOnQuery"]))
        .refine((arr) => {
            const uniqueArr = Array.from(new Set(arr));
            return uniqueArr.length === arr.length;
        }, { message: "Attributes must be unique" })
        .optional(),
});
//#endregion 

export const EntityFieldSchema = z.discriminatedUnion("type", [
    BooleanEntityFieldSchema,
    DateEntityFieldSchema,
    NumberEntityFieldSchema,
    StringEntityFieldSchema,
    UuidEntityFieldSchema,
    CustomEntityFieldSchema,
    LookupEntityFieldSchema,
]);
export type EntityField = z.infer<typeof EntityFieldSchema>;